import _ from "lodash";
import React from "react";
import toast from "react-hot-toast";
import Button from "../../../buttons";
import ThemedModal from "../../../utilities/ThemedModal";
import ReportCategoriesList from "./ReportCategoriesList";
import ReportProductAreasList from "./ReportProductAreasList";
import { useAuth } from "react-oidc-context";
import { toNString } from "../../../../helpers/guids";
import { useCreateReport } from "../hooks/useCreateReport";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { DateInput, FormCheck, FormInput, NumberInput } from "../../../forms";
import { BooleanSelect, ClientBusinessSheetSearchSelect, ClientProjectSearchSelect, ReportAuthorSelect, ReportTemplateSelect, ReportTypesSelect } from "../../../forms/selects";
import { Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { SingleValue } from "react-select";
import { DefReactSelectOption } from "../../../../types/DefReactSelectOption";
import { MasterAccountProjectType } from "../../../../types/MasterAccountProjectType";
import { CheckFieldFormType, ClientReportCreateModalProps, CreateClientReportBaseForm, CreateClientReportForm, CreateClientReportHookForm } from "../types";
import { ClientReportType } from "../../../../types/ClientReportType";
import { ClientReportTypeType } from "../../../../types/ClientReportTypeType";
import { openConfirmModal, ConfirmModalMessageText } from "../../ConfirmModal";
import classNames from "classnames";

export const CreateReportModal = ({
    adviserId,
    allowedReportTypeOptions = null,
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    dialogId,
    dialogTitle,
    dismiss,
    divisionId,
    hideProjectOnReviewSet = true,
    masterAccountId,
    proceed,
    show,
    size = "lg",
    ...rest
} : Readonly<ClientReportCreateModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const { user } = useAuth();
    const { control, formState: { errors }, handleSubmit, reset, setValue, watch } = useForm<CreateClientReportHookForm>();
    const { createReport, isCreating, isAllowedToAssignAuthor, isAllowedToSetDeadlineDate, isAllowedToSetEstimatedMinutes } = useCreateReport(masterAccountId);
    const reviewId = watch('reviewId');
    const isCreatingBusinessSheet = watch('isRecommendationsGenerated');

    const assignReportTypeMappings = (categories: string[], productAreas: string[], reportType: ClientReportTypeType | null) : void => {
        if (reportType) {
            setValue('estimatedMinutesToComplete', reportType?.defaultEstimatedMinutes ?? 30);
        }

        setValue('categories', categories.map((el: string) => ({ value: el, description: el } as CheckFieldFormType)));
        setValue('productAreas', productAreas.map((el: string) => ({ value: el, description: el } as CheckFieldFormType)));
    };

    const onClose = () : void => {
        cancel!();
    };

    const onReportTypeSelected = (selection: SingleValue<DefReactSelectOption<ClientReportTypeType>>) : void => {
        // update the actual field-id that we would be doing originally before overriding the onChange
        setValue('typeId', selection?.value);
        
        // check we got an option selected, otherwise we need to reset values
        if (!selection || !selection.data) {
            return;
        }

        // attempt to pull-out the data we need for assinging the type mappings
        const { data } = selection || {};
        const { mappedCategories: categories, mappedProductAreas: productAreas } = data || { categories: [], productAreas: [] };
        if (categories!.length === 0 && productAreas!.length === 0) {
            setValue('estimatedMinutesToComplete', selection?.data?.defaultEstimatedMinutes ?? 30);
            return;
        }

        // prompt the user to confirm they want their mappings changed as they might have manually set them up before-hand
        // NOTE: do not overwrite what a user has already input. Perhaps we can check if they have already "dirtied" the fields before
        // This should be possible with the react-form-hooks that we are using for this section
        openConfirmModal({
            cancelButtonText: 'No, Manually assign.',
            confirmButtonText: 'Yes, Confirm.',
            dialogTitle: 'Assign default Categories?',
            message: (<ConfirmModalMessageText>{`The Report Type: '${selection.label}' you have selected contains default Category & Product Area mappings. Would you like to assign these to the report?`}</ConfirmModalMessageText>)
        }).then((r) => assignReportTypeMappings(categories!, productAreas!, selection.data!));
    };

    const onSubmit = (form: CreateClientReportHookForm) : void => {
        // lets create the main request we will shift to the back-end
        const baseRequest = { ...form } as CreateClientReportBaseForm;
        const createRequest : CreateClientReportForm = {
            ...baseRequest,
            categories: form.categories.map((el) => el.value),
            productAreas: form.productAreas.map((el) => el.value)
        };

        // make the request and await the response
        toast.promise(createReport(createRequest), {
            loading: 'Creating report document...',
            success: 'Your report has been generated.',
            error: 'There was a problem with generating your report. Unable to complete your request.'
        }).then(
            (r: ClientReportType) => proceed!(r)
        );
    };

    React.useEffect(() => {
        const baseForm = { ...defaultValues } as CreateClientReportBaseForm;
        reset({ 
            ...baseForm,
            categories: defaultValues?.categories.map((el: string) => ({ value: el, description: el } as CheckFieldFormType)),
            productAreas: defaultValues?.productAreas.map((el: string) => ({ value: el, description: el } as CheckFieldFormType))
        });
    }, [ defaultValues ]);
    
    const isFieldsDisabled = isCreating;
    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header closeButton>
                <Modal.Title>{dialogTitle ?? 'Create a New Report'}</Modal.Title>
            </ThemedModal.Header>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body ref={bodyAnimation}>
                    {(!reviewId || (reviewId && hideProjectOnReviewSet === false)) && (
                        <div className="row mb-2">
                            <div className="col">
                                <Controller
                                    name="projectId"
                                    control={control}
                                    rules={({ required: "A Project must be assigned to a Report." })}
                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                        <ClientProjectSearchSelect
                                            adviserId={adviserId}
                                            divisionId={divisionId}
                                            error={errors?.projectId?.message}
                                            isDisabled={isFieldsDisabled}
                                            masterAccountId={masterAccountId}
                                            onChange={(opt) => setValue('projectId', (opt as SingleValue<DefReactSelectOption<MasterAccountProjectType>>)?.value)}
                                            value={value}
                                            userId={toNString(user!.profile.sub)}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    )}
                    <div className="row my-2">
                        <div className="col">
                            <Controller
                                name="reportName"
                                control={control}
                                rules={{ required: "Report Name is required." }}
                                render={({ field: { ref, ...rest } }) => (
                                    <FormInput 
                                        label="Report Name" 
                                        disabled={isFieldsDisabled}
                                        disableAnimations={true} 
                                        errorMessage={errors?.reportName?.message} 
                                        errorAllowRetry={false} 
                                        {...rest} 
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-6">
                            <Controller
                                name="typeId"
                                control={control}
                                rules={{ required: "A Report Type must be selected." }}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <ReportTypesSelect
                                        defaultValue={value}
                                        isDisabled={isFieldsDisabled}
                                        disableAnimations={true}
                                        errorAllowRetry={false}
                                        errorMessage={errors?.typeId?.message}
                                        label="Report Type"
                                        onChange={onReportTypeSelected}
                                        options={allowedReportTypeOptions}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-6">
                            <Controller
                                name="templateId"
                                control={control}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <ReportTemplateSelect
                                        label="Borrow / Template"
                                        defaultValue={value}
                                        isClearable={true}
                                        isDisabled={isFieldsDisabled}
                                        disableAnimations={true}
                                        errorAllowRetry={false}
                                        placeholder="No Template Selected."
                                        onChange={(values: any) => setValue('templateId', values?.value ?? null)}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-6">
                            <Controller
                                name="authorId"
                                control={control}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <ReportAuthorSelect
                                        label="Author"
                                        defaultValue={value}
                                        divisionId={divisionId}
                                        disableAnimations={true}
                                        isClearable={true}
                                        isDisabled={!isAllowedToAssignAuthor || isFieldsDisabled}
                                        placeholder="No Author assigned."
                                        onChange={(opt: SelectOption<string> | null) => setValue('authorId', opt?.value ?? null, { shouldDirty: true })}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                        <div className="col">
                            <Controller
                                name="estimatedMinutesToComplete"
                                control={control}
                                render={({ field: { ref, ...rest } }) => (
                                    <NumberInput 
                                        label="Estimated Minutes" 
                                        disableAnimations={true} 
                                        decimalScale={0} 
                                        disabled={!isAllowedToSetEstimatedMinutes || isFieldsDisabled}
                                        errorMessage={errors?.estimatedMinutesToComplete?.message}
                                        errorAllowRetry={false} 
                                        {...rest} 
                                    />
                                )}
                            />
                        </div>
                        <div className="col">
                            <Controller
                                name="deadlineDate"
                                control={control}
                                render={({ field: { ref, onBlur, onChange, ...rest } }) => (
                                    <DateInput
                                        label="Deadline"
                                        placeholder="Choose Report deadline."
                                        disableAnimations={true}
                                        isClearable
                                        disabled={!isAllowedToSetDeadlineDate || isFieldsDisabled}
                                        onBlur={(value, e) => {
                                            if (value !== "Invalid date") {
                                                setValue('deadlineDate', value);
                                            }
                                        }}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className={classNames({
                            "col-2": isCreatingBusinessSheet,
                            "col-12": !isCreatingBusinessSheet
                        })}>
                            <Controller
                                name="isRecommendationsGenerated"
                                control={control}
                                render={({ field: { ref, value, onChange, ...rest } }) => (
                                    <BooleanSelect
                                        defaultValue={value}
                                        isDisabled={isFieldsDisabled}
                                        disableAnimations={true}
                                        label={isCreatingBusinessSheet ? "Create" : "Create Business Sheet"}
                                        onChange={(values: any) => setValue('isRecommendationsGenerated', values.value, { shouldDirty: true })}
                                        {...rest}
                                    />
                                )}
                            />
                        </div>
                        {isCreatingBusinessSheet && (
                            <div className="col-10">
                                <Controller
                                    name="borrowRecommendationsId"
                                    control={control}
                                    render={({ field: { ref, value, onChange, ...rest } }) => (
                                        <ClientBusinessSheetSearchSelect
                                            allowCreate={false}
                                            label="Borrow from Business Sheet?"
                                            value={value}
                                            disableAnimations={true}
                                            isClearable={true}
                                            isDisabled={isFieldsDisabled}
                                            masterAccountId={masterAccountId}
                                            onChange={(values: any) => setValue('borrowRecommendationsId', values.value, { shouldDirty: true })}
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </div>
                    <div className="row mb-2">
                        <div className="col">
                            <ReportCategoriesList control={control} name="categories" isDisabled={isFieldsDisabled} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <ReportProductAreasList control={control} name="productAreas" isDisabled={isFieldsDisabled} />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" type="submit" disabled={isFieldsDisabled === true}>Create Report</Button>
                    <Button variant="light" onClick={onClose} disabled={isFieldsDisabled === true}>Close</Button>
                </Modal.Footer>
            </form>
        </Modal>
    )
}